// API fetch 래퍼. 401 응답 시 등록된 핸들러 호출 → 라우터가 /lock 리다이렉트 수행.

export interface ApiError {
  status: number;
  error: string;
  body?: unknown;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/**
 * 모든 API 요청의 기본 타임아웃.
 * - 7초: status·notifications 같은 단순 endpoint에 충분한 여유값.
 * - 운영 stuck 재발 시 DevTools Console에서 [locker.fetch] timeout 로그로 즉시 확인 가능.
 */
const REQUEST_TIMEOUT_MS = 7_000;

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  // AbortController로 타임아웃 — fetch가 응답을 영원히 기다리는 것 방지.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  // 진단 로그 — 운영 stuck 발생 시 DevTools Console에서 원인 추적용.
  const startAt = Date.now();
  console.info('[locker.fetch] start', path);
  try {
    const res = await fetch(path, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(init.headers ?? {}),
      },
      credentials: 'same-origin',
      signal: init.signal ?? controller.signal,
    });
    const elapsed = Date.now() - startAt;
    console.info('[locker.fetch] done', path, res.status, `${elapsed}ms`);
    if (res.status === 401 && onUnauthorized) onUnauthorized();
    if (!res.ok) {
      let body: unknown = null;
      try { body = await res.json(); } catch { body = null; }
      const apiError: ApiError = {
        status: res.status,
        error: (body as { error?: string } | null)?.error ?? 'unknown',
        body,
      };
      throw apiError;
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } catch (err) {
    // 타임아웃이나 네트워크 오류는 ApiError 형태로 정규화 — Lock 화면 등이 일관 처리.
    if (err instanceof DOMException && err.name === 'AbortError') {
      console.warn('[locker.fetch] timeout', path);
      const apiError: ApiError = {
        status: 0,
        error: 'request_timeout',
        body: { detail: `${REQUEST_TIMEOUT_MS}ms 내 응답 없음` },
      };
      throw apiError;
    }
    if (err instanceof TypeError) {
      // fetch 자체 실패 — 네트워크 끊김, CORS, DNS 등
      console.warn('[locker.fetch] network', path, err.message);
      const apiError: ApiError = {
        status: 0,
        error: 'network_error',
        body: { detail: err.message },
      };
      throw apiError;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const api = {
  get:  <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : JSON.stringify({}) }),
  put:  <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : JSON.stringify({}) }),
  patch:<T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : JSON.stringify({}) }),
  del:  <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

/** ApiError 식별 타입 가드. */
export function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'status' in err && 'error' in err;
}

/**
 * 서버 에러를 사람이 읽을 수 있는 한국어 메시지로 변환.
 * - invalid_request: zod details에서 첫 번째 필드·사유를 꺼내 노출
 * - invalid_env:     파서가 보고한 line + reason
 * - 기타:            서버 error 키 그대로
 */
export function formatApiError(err: unknown, fallback: string = '요청 실패'): string {
  if (!isApiError(err)) return fallback;
  const body = err.body as Record<string, unknown> | null;

  if (err.error === 'invalid_request') {
    const details = body?.details as Array<{ path: Array<string | number>; message: string }> | undefined;
    if (details && details.length > 0) {
      const d = details[0];
      const path = d.path.length > 0 ? d.path.join('.') : '';
      return path ? `입력 오류 — ${path}: ${d.message}` : `입력 오류 — ${d.message}`;
    }
    return '입력 오류';
  }
  if (err.error === 'invalid_env') {
    const line = body?.line as number | undefined;
    const reason = body?.reason as string | undefined;
    return line ? `.env ${line}행: ${reason ?? '파싱 실패'}` : '.env 파싱 실패';
  }
  return err.error;
}
